import React, { useState, useEffect, useLayoutEffect, useContext, useRef } from 'react';
import { Container, Row, Col, Badge, Button, Card, Form, Table, ProgressBar } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import TasksService from '../services/TasksService';
import Spinner from './Spinner';
import ResponsiveTable from './ResponsiveTable';
import { useNavigate } from 'react-router-dom';
import { FaArrowUp, FaLock, FaCheckCircle, FaExclamationTriangle, FaChevronDown } from 'react-icons/fa';
import '../assets/styles/Tasks.css';

const Tasks = ({ darkMode = false }) => {
    const [tasks, setTasks] = useState([]);
    const [filteredTasks, setFilteredTasks] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [displayStatistics, setDisplayStatistics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const [isPanel, setIsPanel] = useState(false);
    const [theme, setTheme] = useState('light');
    const containerRef = useRef(null);
    const [expandedCards, setExpandedCards] = useState({}); // Track which cards are expanded
    const [predictedApproversByTask, setPredictedApproversByTask] = useState({});
    const [predictionLoadingByTask, setPredictionLoadingByTask] = useState({});

    // Filters
    const [statusFilter, setStatusFilter] = useState('All');
    const [complianceFilter, setComplianceFilter] = useState('All');
    const [escalationFilter, setEscalationFilter] = useState('All');
    const [suspensionFilter, setSuspensionFilter] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('recent');

    // Detect if this component is rendered inside a draggable panel
    useEffect(() => {
        if (!containerRef.current) return;

        const detectPanel = () => {
            // Direct check: is the container inside a draggable-section?
            const isDraggableSection = !!containerRef.current?.closest('.draggable-section');
            
            // Also check for other panel-like containers
            const isPanelContainer = !!containerRef.current?.closest('[class*="panel"]');
            const isDraggable = !!containerRef.current?.closest('[class*="draggable"]');
            
            const shouldBePanel = isDraggableSection || isPanelContainer || isDraggable;
            
            if (shouldBePanel !== isPanel) {
                setIsPanel(shouldBePanel);
            }
        };

        // Detect immediately when container is available
        detectPanel();

        // Also detect after a small delay in case DOM is still settling
        const timer = setTimeout(detectPanel, 50);
        
        const timer2 = setTimeout(detectPanel, 200);

        // Watch for any DOM changes that might affect context
        const observer = new MutationObserver(detectPanel);
        
        if (containerRef.current?.parentElement) {
            observer.observe(containerRef.current.parentElement, {
                childList: true,
                attributes: true,
                subtree: false
            });
        }
        observer.observe(document.body, { childList: true });

        return () => {
            clearTimeout(timer);
            clearTimeout(timer2);
            observer.disconnect();
        };
    }, [isPanel]);

    useEffect(() => {
        // Detect theme from document root
        const checkTheme = () => {
            const currentTheme = document.documentElement.getAttribute('data-theme') || 'light';
            setTheme(currentTheme);
        };
        
        checkTheme();
        
        // Watch for theme changes
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.attributeName === 'data-theme') {
                    checkTheme();
                }
            });
        });
        
        observer.observe(document.documentElement, { attributes: true });
        
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (user?.userId) {
            loadAdminTasks();
        }
    }, [user]);

    useEffect(() => {
        filterAndSortTasks();
    }, [tasks, statusFilter, complianceFilter, escalationFilter, suspensionFilter, searchQuery, sortBy]);

    useEffect(() => {
        updateDisplayStatistics();
    }, [filteredTasks]);

    // Extract task title and description from entityDetails based on entity type
    const extractEntityDetails = (task) => {
        if (!task.entityDetails) return task;
        
        const entity = task.entityDetails;
        const entityType = task.entityType;
        
        let ticketNumber = '';
        let ticketType = '';
        let ticketDescription = '';
        let requestType = '';
        let requestDescription = '';
        let bonusAmount = '';
        
        if (entityType === 'Ticket') {
            ticketNumber = entity.title || '';
            ticketType = entity.ticketTypeName || '';
            ticketDescription = entity.description || '';
        } else if (entityType === 'LeaveRequest') {
            requestType = entity.leaveTypeName || 'Leave Request';
            requestDescription = entity.reason || '';
        } else if (entityType === 'BonusRequest') {
            console.log('BonusRequest entity details:', entity);
            console.log('Available keys:', Object.keys(entity));
            requestType = entity.BonusTypeName || entity.bonusTypeName || entity.Name || 'Bonus Request';
            requestDescription = entity.reason || '';
            bonusAmount = entity.amount || '';
            console.log('Extracted - Type:', requestType, 'Reason:', requestDescription, 'Amount:', bonusAmount);
        }
        
        return {
            ...task,
            ticketNumber,
            ticketType,
            ticketDescription,
            requestType,
            requestDescription,
            bonusAmount
        };
    };

    const loadAdminTasks = async () => {
        try {
            setError(null);
            setLoading(true);
            const [allTasks, stats] = await Promise.all([
                TasksService.getAllTasks(user.userId),
                TasksService.getTaskStatistics()
            ]);
            // Extract entity details into taskName and taskDescription
            const processedTasks = (allTasks || []).map(task => extractEntityDetails(task));
            setTasks(processedTasks);
            setStatistics(stats);
        } catch (error) {
            console.error('Error loading admin tasks:', error);
            setError(error.message || 'Failed to load tasks');
        } finally {
            setLoading(false);
        }
    };

    const filterAndSortTasks = () => {
        let filtered = [...tasks];

        // Apply status filter
        if (statusFilter !== 'All') {
            filtered = filtered.filter(t => t.status === statusFilter);
        }

        // Apply compliance filter
        if (complianceFilter !== 'All') {
            filtered = filtered.filter(t => t.complianceStatus === complianceFilter);
        }

        // Apply escalation filter
        if (escalationFilter === 'Escalated') {
            filtered = filtered.filter(t => t.isEscalated === true);
        } else if (escalationFilter === 'Not Escalated') {
            filtered = filtered.filter(t => t.isEscalated === false);
        }

        // Apply suspension filter
        if (suspensionFilter === 'Suspended') {
            filtered = filtered.filter(t => t.isSuspended === true);
        } else if (suspensionFilter === 'Active') {
            filtered = filtered.filter(t => t.isSuspended === false);
        }

        // Apply search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(t =>
                t.id.toString().includes(query) ||
                t.workflowName?.toLowerCase().includes(query) ||
                t.initiatedByUserName?.toLowerCase().includes(query)
            );
        }

        // Sort
        if (sortBy === 'recent') {
            filtered.sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt));
        } else if (sortBy === 'qos-high') {
            filtered.sort((a, b) => (b.qualityOfServiceScore || 0) - (a.qualityOfServiceScore || 0));
        } else if (sortBy === 'qos-low') {
            filtered.sort((a, b) => (a.qualityOfServiceScore || 0) - (b.qualityOfServiceScore || 0));
        } else if (sortBy === 'priority') {
            const priorityOrder = { 'High': 0, 'Normal': 1, 'Low': 2 };
            filtered.sort((a, b) => (priorityOrder[a.priority] || 99) - (priorityOrder[b.priority] || 99));
        } else if (sortBy === 'transfers') {
            filtered.sort((a, b) => (b.transferCount || 0) - (a.transferCount || 0));
        }

        setFilteredTasks(filtered);
    };

    const updateDisplayStatistics = () => {
        const stats = {
            total: filteredTasks.length,
            inProgress: filteredTasks.filter(t => t.status === 'InProgress').length,
            breached: filteredTasks.filter(t => t.complianceStatus === 'Breached').length,
            atRisk: filteredTasks.filter(t => t.complianceStatus === 'AtRisk').length,
            aging: filteredTasks.filter(t => t.isAging === true).length,
            averageQoS: filteredTasks.length > 0 
                ? filteredTasks.reduce((sum, t) => sum + (t.qualityOfServiceScore || 0), 0) / filteredTasks.length
                : 0
        };
        setDisplayStatistics(stats);
    };

    const handleCardClick = (task) => {
        navigate(`/task-detail/${task.id}`);
    };

    const ensurePredictedApproversLoaded = async (task) => {
        if (!task || task.status !== 'InProgress' || !task.currentStepName || !user?.userId) {
            return;
        }

        if (Object.prototype.hasOwnProperty.call(predictedApproversByTask, task.id)) {
            return;
        }

        setPredictionLoadingByTask(prev => ({ ...prev, [task.id]: true }));
        try {
            const prediction = await TasksService.getPredictedNextApprovers(task.id, user.userId);
            setPredictedApproversByTask(prev => ({ ...prev, [task.id]: prediction || null }));
        } catch {
            setPredictedApproversByTask(prev => ({ ...prev, [task.id]: null }));
        } finally {
            setPredictionLoadingByTask(prev => ({ ...prev, [task.id]: false }));
        }
    };

    const toggleCardExpand = async (taskId) => {
        const isExpanding = !expandedCards[taskId];

        setExpandedCards(prev => ({
            ...prev,
            [taskId]: isExpanding
        }));

        if (isExpanding) {
            const task = filteredTasks.find(t => t.id === taskId);
            await ensurePredictedApproversLoaded(task);
        }
    };

    const getComplianceBadge = (status) => {
        const variants = {
            'OnTrack': 'success',
            'AtRisk': 'warning',
            'Breached': 'danger',
            'Completed': 'info'
        };
        return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
    };

    const getComplianceTextColor = (status) => {
        const colors = {
            'OnTrack': '#198754',
            'AtRisk': '#ffc107',
            'Breached': '#dc3545',
            'Completed': '#0dcaf0'
        };
        return colors[status] || (theme === 'dark' ? '#bbb' : '#666');
    };

    const getStatusBadge = (status) => {
        const variants = {
            'InProgress': 'primary',
            'Completed': 'success',
            'Rejected': 'danger',
            'Cancelled': 'secondary',
            'Terminated': 'warning'
        };
        return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
    };

    const getPriorityBadge = (priority) => {
        const variants = {
            'Urgent': 'danger',      // Red
            'High': 'warning',        // Orange/Yellow
            'Normal': 'info',         // Blue
            'Low': 'secondary'        // Gray
        };
        return <Badge bg={variants[priority] || 'secondary'} text={priority === 'High' ? 'dark' : 'white'}>{priority}</Badge>;
    };

    const getQoSColor = (score) => {
        if (!score) return '#6c757d';
        if (score >= 4.5) return '#28a745';
        if (score >= 3.5) return '#ffc107';
        if (score >= 2.5) return '#fd7e14';
        return '#dc3545';
    };

    const isFinalTaskStatus = (status) => {
        return status === 'Completed' || status === 'Terminated' || status === 'Rejected' || status === 'Cancelled';
    };

    const getFinalTaskOutcomeText = (status, completedAt) => {
        const labels = {
            Completed: 'Completed',
            Rejected: 'Rejected',
            Cancelled: 'Cancelled',
            Terminated: 'Terminated'
        };

        const actionLabel = labels[status] || status || 'Finished';

        if (!completedAt) {
            return actionLabel;
        }

        try {
            const completed = new Date(completedAt);
            if (isNaN(completed.getTime())) return actionLabel;
            return `${actionLabel} on ${completed.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            })}`;
        } catch {
            return actionLabel;
        }
    };

    const getTimeRemaining = (dueDate, taskStatus = null, completedAt = null) => {
        // If task is completed, show "Completed" instead of checking time remaining
        if (taskStatus === 'Completed' || taskStatus === 'Terminated' || taskStatus === 'Rejected' || taskStatus === 'Cancelled') {
            if (completedAt) {
                try {
                    const completed = new Date(completedAt);
                    return `✓ ${completed.toLocaleDateString()}`;
                } catch {
                    return `✓ ${taskStatus}`;
                }
            }
            return `✓ ${taskStatus}`;
        }
        
        if (!dueDate) return 'N/A';
        try {
            const now = new Date();
            const due = new Date(dueDate);
            
            // Invalid date check
            if (isNaN(due.getTime())) return 'N/A';
            
            const diff = due - now;
            
            if (diff < 0) return 'Overdue';
            
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            
            if (days > 0) return `${days}d ${hours}h`;
            if (hours > 0) return `${hours}h ${minutes}m`;
            return `${minutes}m`;
        } catch {
            return 'N/A';
        }
    };

    const getRetentionTime = (suspendedTime) => {
        if (!suspendedTime) return 'N/A';
        try {
            const parts = suspendedTime.match(/(\d+):(\d+):(\d+)/);
            if (!parts) return suspendedTime;
            const hours = parseInt(parts[1]);
            const minutes = parseInt(parts[2]);
            
            if (hours > 24) {
                const days = Math.floor(hours / 24);
                const remainingHours = hours % 24;
                return `${days}d ${remainingHours}h`;
            }
            if (hours > 0) return `${hours}h ${minutes}m`;
            return `${minutes}m`;
        } catch {
            return suspendedTime;
        }
    };

    const formatSlaDueDate = (slaDueDate) => {
        if (!slaDueDate) return 'N/A';
        try {
            const due = new Date(slaDueDate);
            if (isNaN(due.getTime())) return 'N/A';
            return due.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch {
            return 'N/A';
        }
    };

    const formatDateTime = (dateValue) => {
        if (!dateValue) return 'N/A';
        try {
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return 'N/A';
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch {
            return 'N/A';
        }
    };

    const getTicketAge = (startedAt) => {
        if (!startedAt) return 'N/A';
        try {
            const start = new Date(startedAt);
            if (isNaN(start.getTime())) return 'N/A';

            const now = new Date();
            const diff = now - start;
            if (diff < 0) return '0m';

            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

            if (days > 0) return `${days}d ${hours}h`;
            if (hours > 0) return `${hours}h ${minutes}m`;
            return `${minutes}m`;
        } catch {
            return 'N/A';
        }
    };

    const isActiveAgingTask = (task) => {
        return task.isAging === true &&
            task.status !== 'Completed' &&
            task.status !== 'Closed' &&
            task.status !== 'Cancelled' &&
            task.status !== 'Rejected' &&
            task.status !== 'Terminated';
    };

    const getComplianceProgress = (instance) => {
        // Use SLADueDate if available, otherwise fallback to OLADueDate or DueDate
        const dueDate = instance.sLADueDate || instance.oLADueDate || instance.dueDate;
        const startDate = instance.startedAt;
        
        // If no dates available, can't calculate progress
        if (!dueDate || !startDate) return 0;
        
        const start = new Date(startDate);
        const due = new Date(dueDate);
        
        // For completed tasks, use completion time. For active tasks, use current time
        let timepoint = new Date();
        if (instance.status === 'Completed' || instance.status === 'Terminated' || instance.status === 'Rejected' || instance.status === 'Cancelled') {
            if (instance.completedAt) {
                timepoint = new Date(instance.completedAt);
            }
        }
        
        const total = due - start;
        if (total <= 0) return 0; // Invalid date range
        
        const elapsed = timepoint - start;
        const progress = (elapsed / total) * 100;
        
        // Return actual progress (including >100% for overdue detection)
        // Don't cap at 100 here - let color logic handle overdue detection
        return Math.max(0, progress);
    };

    const getStepOlaProgress = (step) => {
        if (!step?.dueDate || !step?.stepStartDate) return null;

        const start = new Date(step.stepStartDate);
        const due = new Date(step.dueDate);

        if (isNaN(start.getTime()) || isNaN(due.getTime())) return null;

        const total = due - start;
        if (total <= 0) return null;

        let timepoint = new Date();
        if (step.status !== 'pending' && step.stepEndDate) {
            const endDate = new Date(step.stepEndDate);
            if (!isNaN(endDate.getTime())) {
                timepoint = endDate;
            }
        }

        const elapsed = timepoint - start;
        return Math.max(0, (elapsed / total) * 100);
    };

    const getStepOverdueDuration = (step) => {
        if (!step?.dueDate) return null;

        const due = new Date(step.dueDate);
        if (isNaN(due.getTime())) return null;

        let timepoint = new Date();
        if (step.status !== 'pending' && step.stepEndDate) {
            const endDate = new Date(step.stepEndDate);
            if (!isNaN(endDate.getTime())) {
                timepoint = endDate;
            }
        }

        const overdueMs = timepoint - due;
        if (overdueMs <= 0) return null;

        const days = Math.floor(overdueMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((overdueMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((overdueMs % (1000 * 60 * 60)) / (1000 * 60));

        if (days > 0) return `${days}d ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    const formatDuration = (diffMs) => {
        if (!Number.isFinite(diffMs) || diffMs <= 0) return '0m';

        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        if (days > 0) return `${days}d ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    const getTransitionDurationText = (previousStep, currentStep) => {
        if (!previousStep || !currentStep) return '';

        const startRaw = previousStep.date || previousStep.stepEndDate || previousStep.stepStartDate;
        const endRaw = currentStep.date || currentStep.stepEndDate;
        if (!startRaw || !endRaw) return '';

        const start = new Date(startRaw);
        const end = new Date(endRaw);
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';

        return formatDuration(end - start);
    };

    const getStepOlaVariant = (progress) => {
        if (progress === null || progress === undefined) return 'secondary';
        if (progress >= 100) return 'danger';
        if (progress >= 70) return 'warning';
        return 'info';
    };

    // Timeline helper functions
    const getPendingApproverText = (taskId) => {
        if (predictionLoadingByTask[taskId]) {
            return 'Loading...';
        }

        const candidates = predictedApproversByTask[taskId]?.candidates || [];
        if (!candidates.length) return null;

        const names = candidates
            .map(c => c.userName)
            .filter(Boolean);

        if (!names.length) return null;
        if (names.length === 1) return names[0];

        const preview = names.slice(0, 3).join(', ');
        const remaining = names.length - 3;
        return remaining > 0
            ? `${preview} +${remaining} more`
            : preview;
    };

    const buildTimelineSteps = (task) => {
        const steps = [];
        let lastStepDate = task.startedAt || null;
        
        // Always start with creation step
        if (task.startedAt) {
            steps.push({
                title: 'Request Created',
                performer: `Created by ${task.initiatedByUserName}`,
                date: task.startedAt,
                status: 'completed',
                action: 'Created',
                dueDate: null,
                stepStartDate: null,
                stepEndDate: task.startedAt
            });
        }
        
        // Add all workflow activities/actions from the activities array
        if (task.activities && Array.isArray(task.activities) && task.activities.length > 0) {
            task.activities.forEach(activity => {
                const performedAt = activity.performedAt || null;
                steps.push({
                    title: activity.workflowStepName || activity.action || 'Step',
                    performer: activity.performedByUserName, 
                    date: performedAt,
                    status: 'completed',
                    action: activity.action,
                    comments: activity.comments,
                    dueDate: activity.dueDate || null,
                    stepStartDate: lastStepDate,
                    stepEndDate: performedAt
                });

                if (performedAt) {
                    lastStepDate = performedAt;
                }
            });
        }
        
        // Add current pending step if in progress (and not already in activities)
        if (task.currentStepName && task.status === 'InProgress') {
            const alreadyExists = steps.some(s => 
                s.title === task.currentStepName || s.action === task.currentStepName
            );
            if (!alreadyExists) {
                steps.push({
                    title: task.currentStepName,
                    performer: getPendingApproverText(task.id),
                    date: null,
                    status: 'pending',
                    action: 'Pending approval',
                    dueDate: task.oLADueDate || task.olaDueDate || task.OLADueDate || task.dueDate || null,
                    stepStartDate: lastStepDate || task.startedAt || null,
                    stepEndDate: null
                });
            }
        }
        
        return steps.slice(0, 5); // Show only first 5 steps for compact display
    };

    const renderCompactTimeline = (task) => {
        const timelineSteps = buildTimelineSteps(task);
        
        // Always show timeline if there's at least creation step
        if (timelineSteps.length === 0) {
            return (
                <div className={`compact-timeline theme-${theme} mt-2 pt-2 pb-2 border-top`} 
                     style={{ borderTopColor: theme === 'dark' ? '#444' : '#e9ecef', minHeight: '0' }}>
                    <div style={{ fontSize: '12px', color: theme === 'dark' ? '#aaa' : '#999' }}>
                        No timeline data available
                    </div>
                </div>
            );
        }
        
        const formatDate = (date) => {
            if (!date) return '';
            const d = new Date(date);
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        };

        const keepSingleRow = timelineSteps.length <= 4;
        const connectorWidth = keepSingleRow ? 56 : 72;
        
        return (
            <div className={`compact-timeline theme-${theme} mt-2 pt-2 border-top`} 
                 style={{ 
                     borderTopColor: theme === 'dark' ? '#444' : '#e9ecef',
                     width: '100%',
                     display: 'block'
                 }}>
                <div
                    className="timeline-compact-container"
                    style={{
                        width: '100%',
                        display: 'flex',
                        flexWrap: keepSingleRow ? 'nowrap' : 'wrap',
                        overflowX: 'visible'
                    }}
                >
                    {timelineSteps.map((step, index) => {
                        const olaProgress = getStepOlaProgress(step);
                        const overdueDuration = getStepOverdueDuration(step);
                        const hasNextStep = index < timelineSteps.length - 1;
                        const previousStep = index > 0 ? timelineSteps[index - 1] : null;
                        const transitionDuration = hasNextStep ? getTransitionDurationText(previousStep, step) : '';
                        const connectorDuration = transitionDuration;

                        return (
                        <React.Fragment key={index}>
                        <div className="timeline-compact-step" style={{ 
                            flex: `1 1 ${Math.max(70, 100 / timelineSteps.length)}px`,
                            minWidth: '70px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            padding: '0 4px'
                        }}>
                            {/* Status circle */}
                            <div 
                                className={`timeline-compact-circle status-${step.status}`}
                                style={{
                                    backgroundColor: 
                                        step.status === 'completed' ? '#28a745' :
                                        step.status === 'pending' ? '#ffc107' : '#6c757d',
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '50%',
                                    margin: '0 auto 6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    border: '2px solid ' + (theme === 'dark' ? '#2a2a2a' : '#f9fafb'),
                                    flexShrink: 0
                                }}
                                title={step.title}
                            >
                                {step.status === 'completed' ? '✓' : 
                                 step.status === 'pending' ? '⏳' : '○'}
                            </div>
                            
                            {/* Action/Step name */}
                            <div 
                                className="timeline-compact-label"
                                style={{
                                    textAlign: 'center',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    lineHeight: '1.3',
                                    wordBreak: 'break-word',
                                    color: theme === 'dark' ? '#e0e0e0' : '#333',
                                    marginBottom: '2px',
                                    minHeight: '22px'
                                }}
                                title={step.action || step.title}
                            >
                                {step.action || step.title}
                            </div>
                            
                            {/* Performer name */}
                            {step.performer && (
                                <div 
                                    style={{
                                        textAlign: 'center',
                                        fontSize: '9px',
                                        color: theme === 'dark' ? '#aaa' : '#666',
                                        lineHeight: '1.2',
                                        wordBreak: 'break-word',
                                        marginBottom: '2px',
                                        minHeight: '18px'
                                    }}
                                    title={step.performer}
                                >
                                    {step.performer.length > 20 ? step.performer.substring(0, 20) + '...' : step.performer}
                                </div>
                            )}
                            
                            {/* Date (or overdue if no action date) */}
                            {(step.date || overdueDuration) && (
                                <div 
                                    style={{
                                        textAlign: 'center',
                                        fontSize: '8px',
                                        color: step.date
                                            ? (theme === 'dark' ? '#888' : '#999')
                                            : (theme === 'dark' ? '#ff9ea8' : '#dc3545'),
                                        marginTop: '2px',
                                        minHeight: '14px'
                                    }}
                                    title={step.date ? formatDate(step.date) : `Overdue ${overdueDuration}`}
                                >
                                    {step.date ? formatDate(step.date) : `Overdue ${overdueDuration}`}
                                </div>
                            )}
                            
                            {/* Comments indicator */}
                            {step.comments && (
                                <div 
                                    style={{
                                        textAlign: 'center',
                                        fontSize: '10px',
                                        marginTop: '2px',
                                        color: theme === 'dark' ? '#ffc107' : '#ffc107'
                                    }}
                                    title={step.comments}
                                >
                                    💬
                                </div>
                            )}

                            {olaProgress !== null && (
                                <div style={{ width: '100%', maxWidth: '90px', marginTop: '4px', marginLeft: 'auto', marginRight: 'auto' }}>
                                    <div style={{
                                        fontSize: '8px',
                                        textAlign: 'center',
                                        color: theme === 'dark' ? '#aaa' : '#666',
                                        marginBottom: '2px'
                                    }}>
                                        {`OLA ${Math.round(Math.min(olaProgress, 100))}%`}
                                    </div>
                                    <ProgressBar
                                        now={Math.min(olaProgress, 100)}
                                        max={100}
                                        variant={getStepOlaVariant(olaProgress)}
                                        style={{ height: '4px', width: '100%' }}
                                    />
                                </div>
                            )}
                        </div>
                        {hasNextStep && (
                            <div
                                className="timeline-compact-connector"
                                style={{
                                    flex: `0 0 ${connectorWidth}px`,
                                    alignSelf: 'flex-start',
                                    marginTop: '3px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start'
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: '8px',
                                        lineHeight: '1',
                                        minHeight: '10px',
                                        color: theme === 'dark' ? '#a7d7b6' : '#1f7a3d',
                                        marginBottom: '3px',
                                        whiteSpace: 'nowrap'
                                    }}
                                    title={connectorDuration ? `Time to next node: ${connectorDuration}` : ''}
                                >
                                    {connectorDuration}
                                </div>
                                <div
                                    style={{
                                        width: 'calc(100% + 20px)',
                                        height: '2px',
                                        borderRadius: '2px',
                                        background: '#28a745',
                                        marginTop: '6px',
                                        marginLeft: '-10px'
                                    }}
                                />
                            </div>
                        )}
                    </React.Fragment>
                    );})}
                </div>
            </div>
        );
    };

    const renderPanelCards = () => {
        if (filteredTasks.length === 0) {
            return (
                <div className="text-center py-5">
                    <p className="text-muted">No tasks match your search criteria</p>
                </div>
            );
        }

        return (
            <div className={`task-cards-container theme-${theme}`}>
                {filteredTasks.slice(0, 10).map((task) => (
                    <Card 
                        key={task.id}
                        className={`task-card task-card-panel mb-2 cursor-pointer theme-${theme}`}
                        onClick={() => toggleCardExpand(task.id)}
                        style={{
                            borderLeft: `4px solid ${getQoSColor(task.qualityOfServiceScore)}`,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            maxHeight: expandedCards[task.id] ? '1000px' : '220px',
                            overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateX(5px)';
                            e.currentTarget.style.boxShadow = theme === 'dark' 
                                ? '0 2px 8px rgba(255, 255, 255, 0.1)'
                                : '0 2px 8px rgba(0, 0, 0, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateX(0)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    >
                        <Card.Body className="py-2 px-1">
                            {/* Row 1: Task Info + Badges */}
                            <Row className="align-items-center mb-3">
                                <Col md={12} className="ps-1">
                                    <div className="task-info-wrapper">
                                        {/* Chevron */}
                                        <FaChevronDown 
                                            className="task-info-chevron"
                                            style={{ 
                                                transform: expandedCards[task.id] ? 'rotate(180deg)' : 'rotate(0deg)'
                                            }} 
                                        />
                                        {/* Task Info and Details */}
                                        <div className="task-info-content">
                                            {/* Header Row: Task # and Badges */}
                                            <div className="task-header-row" style={{ gap: '12px', alignItems: 'center' }}>
                                                <h6 className={`task-number mb-0 theme-${theme}`} style={{ whiteSpace: 'nowrap' }}>
                                                    Task #{task.id}
                                                </h6>
                                                {/* Badges on same line as Task # */}
                                                <div className="task-badges-container">
                                                    {getStatusBadge(task.status)}
                                                    {getPriorityBadge(task.priority)}
                                                    {getComplianceBadge(task.complianceStatus)}
                                                    {task.isAging && <Badge bg="danger" style={{ fontSize: '10px' }}>Aging</Badge>}
                                                    {task.isEscalated && <Badge bg="danger" style={{ fontSize: '10px' }}>Escalated</Badge>}
                                                    {task.isSuspended && <Badge bg="warning" style={{ fontSize: '10px' }}>Suspended</Badge>}
                                                </div>
                                            </div>
                                            {/* Details Below */}
                                            <div className="task-details-section"
                                                onClick={() => navigate(`/task-detail/${task.id}`)}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                style={{ marginTop: '4px' }}
                                            >
                                                {task.entityType === 'Ticket' ? (
                                                    <>
                                                        {/* Date on separate line */}
                                                        {task.startedAt && (
                                                            <div className={`task-detail-item first task-detail-ticket-date fs-8 theme-${theme}`}>
                                                                {formatDateTime(task.startedAt)}
                                                            </div>
                                                        )}
                                                        {/* Entity Type with Type Name */}
                                                        {task.ticketType && (
                                                            <div className={`task-detail-item second task-detail-ticket-type fs-7 theme-${theme}`}>
                                                                Ticket ({task.ticketType})
                                                            </div>
                                                        )}
                                                        <div className={`task-detail-item fs-8 theme-${theme}`}>
                                                            By: <strong>{task.initiatedByUserName}</strong>
                                                        </div>
                                                        {/* Title on separate line */}
                                                        {task.ticketNumber && (
                                                            <div className={`task-detail-item task-detail-ticket-number fs-7 theme-${theme}`}>
                                                                {task.ticketNumber}
                                                            </div>
                                                        )}
                                                        {/* Description on separate line */}
                                                        {task.ticketDescription && (
                                                            <div className={`task-detail-item third task-detail-description fs-7 theme-${theme}`}>
                                                                {task.ticketDescription}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : task.entityType === 'LeaveRequest' ? (
                                                    <>
                                                        {/* Date on separate line */}
                                                        {task.startedAt && (
                                                            <div className={`task-detail-item first task-detail-ticket-date fs-8 theme-${theme}`}>
                                                                {formatDateTime(task.startedAt)}
                                                            </div>
                                                        )}
                                                        {/* Leave Request Type with Type Name */}
                                                        {task.requestType && (
                                                            <div className={`task-detail-item second task-detail-ticket-type fs-7 theme-${theme}`}>
                                                                Leave Request ({task.requestType})
                                                            </div>
                                                        )}
                                                        <div className={`task-detail-item fs-8 theme-${theme}`}>
                                                            By: <strong>{task.initiatedByUserName}</strong>
                                                        </div>
                                                        {/* Reason on separate line */}
                                                        {task.requestDescription && (
                                                            <div className={`task-detail-item task-detail-ticket-number fs-7 theme-${theme}`}>
                                                                {task.requestDescription}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        {/* Date on separate line */}
                                                        {task.startedAt && (
                                                            <div className={`task-detail-item first task-detail-ticket-date fs-8 theme-${theme}`}>
                                                                {formatDateTime(task.startedAt)}
                                                            </div>
                                                        )}
                                                        {/* Bonus Request Type with Type Name */}
                                                        {task.requestType && (
                                                            <div className={`task-detail-item second task-detail-ticket-type fs-7 theme-${theme}`}>
                                                                Bonus Request ({task.requestType})
                                                            </div>
                                                        )}
                                                        <div className={`task-detail-item fs-8 theme-${theme}`}>
                                                            By: <strong>{task.initiatedByUserName}</strong>
                                                        </div>
                                                        {/* Reason on separate line */}
                                                        {task.requestDescription && (
                                                            <div className={`task-detail-item task-detail-ticket-number fs-7 theme-${theme}`}>
                                                                {task.requestDescription}
                                                            </div>
                                                        )}
                                                        {/* Amount on separate line */}
                                                        {task.bonusAmount && (
                                                            <div className={`task-detail-item third task-detail-description fs-7 theme-${theme}`}>
                                                                Amount: ${task.bonusAmount}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            {/* Row 2: QoS, Time, and Progress */}
                            <Row className="align-items-center g-2">
                                <Col md={12} className="ps-1">
                                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginTop: '4px' }}>
                                        <div style={{ flex: '0 0 auto' }}>
                                            <small style={{
                                                color: theme === 'dark' ? '#ddd' : '#333',
                                                fontSize: '11px',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                <strong>QoS:</strong> {task.qualityOfServiceScore?.toFixed(2) || 'N/A'}
                                            </small>
                                        </div>
                                        <div style={{ flex: '0 0 auto' }}>
                                            <small style={{
                                                color: theme === 'dark' ? '#ddd' : '#333',
                                                fontSize: '11px',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                <strong>SLA:</strong> {Math.round(Math.min(getComplianceProgress(task), 100))}%
                                            </small>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1, minWidth: '150px' }}>
                                            <ProgressBar 
                                                now={Math.min(getComplianceProgress(task), 100)}
                                                max={100}
                                                variant={
                                                    (() => {
                                                        const progress = getComplianceProgress(task);
                                                        if (task.status === 'Completed') {
                                                            return 'success';
                                                        } else if (progress >= 100) {
                                                            return 'danger';
                                                        } else if (progress >= 50) {
                                                            return 'warning';
                                                        }
                                                        return 'info';
                                                    })()
                                                }
                                                style={{ height: '5px', flex: 1, minWidth: '80px', maxWidth: '150px' }}
                                            />
                                        </div>
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginTop: '4px',
                                        fontSize: '11px',
                                        color: theme === 'dark' ? '#ddd' : '#333'
                                    }}>
                                        {(() => {
                                            const slaDueDateValue = task.sLADueDate || task.slaDueDate || task.SLADueDate;
                                            const isFinalStatus = isFinalTaskStatus(task.status);
                                            return (
                                        <span>
                                            <strong>
                                                SLA due date:
                                            </strong>{' '}
                                            {formatSlaDueDate(slaDueDateValue)}{' '}
                                            <strong>
                                                {isFinalStatus
                                                    ? `(✓ ${getFinalTaskOutcomeText(task.status, task.completedAt)})`
                                                    : `(time remaining: ${getTimeRemaining(slaDueDateValue, task.status, task.completedAt)})`}
                                            </strong>
                                        </span>
                                            );
                                        })()}
                                        {isActiveAgingTask(task) && (
                                            <span><strong>Age:</strong> {getTicketAge(task.startedAt)}</span>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                            {/* Timeline Section - Only show when expanded */}
                            {expandedCards[task.id] && renderCompactTimeline(task)}
                        </Card.Body>
                    </Card>
                ))}
            </div>
        );
    };

    if (loading) {
        return <Spinner active={loading} />;
    }

    // Emergency safety check: if we're clearly in a panel context, force panel view
    // This prevents the admin table view from accidentally covering the dashboard
    const shouldShowPanel = isPanel || !!containerRef.current?.closest('.draggable-section');

    // Render panel view (cards layout for user dashboard)
    if (shouldShowPanel) {
        return (
            <div ref={containerRef} data-tasks-container="true" className={`tasks-panel theme-${theme} py-3`} style={{
                padding: '20px',
                borderRadius: '8px',
                position: 'relative'
            }}>

                {error && <div className="alert alert-danger mb-3">{error}</div>}

                {/* Mini Statistics */}
                {displayStatistics && (
                    <div className="mb-3">
                        <Row className="g-2">
                            <Col md={4}>
                                <div className={`stat-mini-card theme-${theme}`} style={{
                                    padding: '10px',
                                    borderRadius: '6px',
                                    textAlign: 'center',
                                    backgroundColor: theme === 'dark' 
                                        ? 'rgba(45, 45, 45, 0.4)' 
                                        : 'rgba(255, 255, 255, 0.5)',
                                    border: theme === 'dark' 
                                        ? '1px solid rgba(255, 255, 255, 0.1)' 
                                        : '1px solid rgba(0, 0, 0, 0.1)'
                                }}>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
                                        {displayStatistics.total}
                                    </div>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        color: theme === 'dark' ? '#aaa' : '#666',
                                        marginTop: '4px'
                                    }}>
                                        Total Tasks
                                    </div>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className={`stat-mini-card theme-${theme}`} style={{
                                    padding: '10px',
                                    borderRadius: '6px',
                                    textAlign: 'center',
                                    backgroundColor: theme === 'dark' 
                                        ? 'rgba(45, 45, 45, 0.4)' 
                                        : 'rgba(255, 255, 255, 0.5)',
                                    border: theme === 'dark' 
                                        ? '1px solid rgba(255, 255, 255, 0.1)' 
                                        : '1px solid rgba(0, 0, 0, 0.1)'
                                }}>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ffc107' }}>
                                        {displayStatistics.atRisk}
                                    </div>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        color: theme === 'dark' ? '#aaa' : '#666',
                                        marginTop: '4px'
                                    }}>
                                        At Risk
                                    </div>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className={`stat-mini-card theme-${theme}`} style={{
                                    padding: '10px',
                                    borderRadius: '6px',
                                    textAlign: 'center',
                                    backgroundColor: theme === 'dark' 
                                        ? 'rgba(45, 45, 45, 0.4)' 
                                        : 'rgba(255, 255, 255, 0.5)',
                                    border: theme === 'dark' 
                                        ? '1px solid rgba(255, 255, 255, 0.1)' 
                                        : '1px solid rgba(0, 0, 0, 0.1)'
                                }}>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc3545' }}>
                                        {displayStatistics.breached}
                                    </div>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        color: theme === 'dark' ? '#aaa' : '#666',
                                        marginTop: '4px'
                                    }}>
                                        Breached
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                )}

                {/* Quick Filters */}
                <div className="mb-3" style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap'
                }}>
                    <Form.Select 
                        size="sm" 
                        value={statusFilter} 
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className={`theme-${theme}`}
                        style={{ 
                            width: 'auto', 
                            flex: '1', 
                            minWidth: '120px',
                            backgroundColor: theme === 'dark' ? '#333' : '#fff',
                            color: theme === 'dark' ? '#fff' : '#000',
                            borderColor: theme === 'dark' ? '#555' : '#ddd'
                        }}
                    >
                        <option>All Status</option>
                        <option>InProgress</option>
                        <option>Completed</option>
                    </Form.Select>
                    <Form.Select 
                        size="sm" 
                        value={complianceFilter} 
                        onChange={(e) => setComplianceFilter(e.target.value)}
                        className={`theme-${theme}`}
                        style={{ 
                            width: 'auto', 
                            flex: '1', 
                            minWidth: '120px',
                            backgroundColor: theme === 'dark' ? '#333' : '#fff',
                            color: theme === 'dark' ? '#fff' : '#000',
                            borderColor: theme === 'dark' ? '#555' : '#ddd'
                        }}
                    >
                        <option>All Compliance</option>
                        <option>OnTrack</option>
                        <option>AtRisk</option>
                        <option>Breached</option>
                    </Form.Select>
                </div>

                {/* Task Cards */}
                {renderPanelCards()}
            </div>
        );
    }

    return (
        <Container ref={containerRef} fluid data-tasks-container="true" className="admin-tasks-page py-4">
            {error && <div className="alert alert-danger">{error}</div>}

            {/* Statistics Cards */}
            {displayStatistics && (
                <Row className="mb-4">
                    <Col md={2} className="mb-3">
                        <Card className="stat-card">
                            <Card.Body>
                                <div className="stat-value">{displayStatistics.total}</div>
                                <div className="stat-label">Total Tasks</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2} className="mb-3">
                        <Card className="stat-card">
                            <Card.Body>
                                <div className="stat-value text-primary">{displayStatistics.inProgress}</div>
                                <div className="stat-label">In Progress</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2} className="mb-3">
                        <Card className="stat-card">
                            <Card.Body>
                                <div className="stat-value text-danger">{displayStatistics.breached}</div>
                                <div className="stat-label">Breached</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2} className="mb-3">
                        <Card className="stat-card">
                            <Card.Body>
                                <div className="stat-value text-warning">{displayStatistics.atRisk}</div>
                                <div className="stat-label">At Risk</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2} className="mb-3">
                        <Card className="stat-card">
                            <Card.Body>
                                <div className="stat-value text-danger">{displayStatistics.aging}</div>
                                <div className="stat-label">Aging</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2} className="mb-3">
                        <Card className="stat-card">
                            <Card.Body>
                                <div className="stat-value">{displayStatistics.averageQoS?.toFixed(2)}</div>
                                <div className="stat-label">Avg QoS</div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Filters */}
            <Card className="mb-4">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Search (ID, Workflow, User)</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Search..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Status</Form.Label>
                                <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                    <option>All</option>
                                    <option>InProgress</option>
                                    <option>Completed</option>
                                    <option>Rejected</option>
                                    <option>Cancelled</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Compliance</Form.Label>
                                <Form.Select value={complianceFilter} onChange={(e) => setComplianceFilter(e.target.value)}>
                                    <option>All</option>
                                    <option>OnTrack</option>
                                    <option>AtRisk</option>
                                    <option>Breached</option>
                                    <option>Completed</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Escalation</Form.Label>
                                <Form.Select value={escalationFilter} onChange={(e) => setEscalationFilter(e.target.value)}>
                                    <option>All</option>
                                    <option>Escalated</option>
                                    <option>Not Escalated</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Status</Form.Label>
                                <Form.Select value={suspensionFilter} onChange={(e) => setSuspensionFilter(e.target.value)}>
                                    <option>All</option>
                                    <option>Active</option>
                                    <option>Suspended</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Sort By</Form.Label>
                                <Form.Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                                    <option value="recent">Most Recent</option>
                                    <option value="qos-high">QoS: High to Low</option>
                                    <option value="qos-low">QoS: Low to High</option>
                                    <option value="priority">Priority</option>
                                    <option value="transfers">Transfers Count</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="g-3 mt-2">
                        <Col md={12} className="d-flex gap-2">
                            <Button variant="primary" onClick={loadAdminTasks}>
                                Refresh
                            </Button>
                            <Button variant="outline-secondary" onClick={() => {
                                setStatusFilter('All');
                                setComplianceFilter('All');
                                setEscalationFilter('All');
                                setSuspensionFilter('All');
                                setSearchQuery('');
                                setSortBy('recent');
                            }}>
                                Clear Filters
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Tasks Table */}
            <Card>
                <Card.Body className="p-0">
                    {filteredTasks.length === 0 ? (
                        <div className="p-4 text-center text-muted">
                            No tasks found matching the selected filters
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover striped className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ cursor: 'pointer' }}>ID</th>
                                        <th>Workflow</th>
                                        <th>Initiator</th>
                                        <th>Status</th>
                                        <th>Priority</th>
                                        <th>Compliance</th>
                                        <th>QoS Score</th>
                                        <th>Transfers</th>
                                        <th>Retention</th>
                                        <th>Flags</th>
                                        <th>SLA Progress</th>
                                        <th>Time Remaining</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTasks.map((task) => (
                                        <tr 
                                            key={task.id}
                                            onClick={() => navigate(`/task-detail/${task.id}`)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td><strong>#{task.id}</strong></td>
                                            <td>{task.workflowName}</td>
                                            <td>{task.initiatedByUserName}</td>
                                            <td>{getStatusBadge(task.status)}</td>
                                            <td>{getPriorityBadge(task.priority)}</td>
                                            <td>{getComplianceBadge(task.complianceStatus)}</td>
                                            <td>
                                                {task.qualityOfServiceScore ? (
                                                    <span style={{ color: getQoSColor(task.qualityOfServiceScore) }}>
                                                        <strong>{task.qualityOfServiceScore.toFixed(2)}</strong>
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">N/A</span>
                                                )}
                                            </td>
                                            <td>
                                                <Badge bg={task.transferCount > 0 ? 'warning' : 'light'} text={task.transferCount === 0 ? 'dark' : 'dark'}>
                                                    {task.transferCount || 0}
                                                </Badge>
                                            </td>
                                            <td>
                                                <small>{getRetentionTime(task.totalSuspendedTime)}</small>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                    {task.isEscalated && (
                                                        <FaArrowUp title="Escalated" style={{ color: '#dc3545', fontSize: '12px' }} />
                                                    )}
                                                    {task.isSuspended && (
                                                        <FaLock title="Suspended" style={{ color: '#0d6efd', fontSize: '12px' }} />
                                                    )}
                                                    {task.wasBreachedButRecovered && (
                                                        <FaCheckCircle title="Recovered from breach" style={{ color: '#198754', fontSize: '12px' }} />
                                                    )}
                                                    {task.dependentOnWorkflowInstanceId && (
                                                        <FaExclamationTriangle title="Has dependencies" style={{ color: '#ffc107', fontSize: '12px' }} />
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ minWidth: '150px' }}>
                                                    {(() => {
                                                        // Determine actual color based on status and time
                                                        const dueDate = task.sLADueDate || task.oLADueDate || task.dueDate;
                                                        const progress = getComplianceProgress(task);
                                                        let color = 'info'; // Default: BLUE for under 100%
                                                        
                                                        // For completed tasks: always GREEN
                                                        if (task.status === 'Completed') {
                                                            color = 'success';
                                                        }
                                                        // For active tasks: check if 100% (overdue) or at-risk
                                                        else if (task.status !== 'Completed') {
                                                            if (progress >= 100) {
                                                                // 100% or more = RED (Overdue/Breached)
                                                                color = 'danger';
                                                            } else if (progress >= 50) {
                                                                // 50-99% = YELLOW (At-risk)
                                                                color = 'warning';
                                                            }
                                                            // Under 50% = stays BLUE (on-track)
                                                        }
                                                        
                                                        return (
                                                            <div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                    <small style={{ minWidth: '35px', fontWeight: 'bold' }}>
                                                                        {Math.round(Math.min(progress, 100))}%
                                                                    </small>
                                                                    <ProgressBar
                                                                        now={Math.min(progress, 100)}
                                                                        max={100}
                                                                        variant={color}
                                                                        style={{ height: '20px', flex: 1 }}
                                                                    />
                                                                </div>
                                                                {isActiveAgingTask(task) && (
                                                                    <div style={{
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        marginTop: '4px',
                                                                        fontSize: '11px',
                                                                        color: '#666'
                                                                    }}>
                                                                        <span><strong>SLA:</strong> {formatSlaDueDate(task.sLADueDate || task.slaDueDate || task.SLADueDate)}</span>
                                                                        <span><strong>Age:</strong> {getTicketAge(task.startedAt)}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </td>
                                            <td>
                                                {(() => {
                                                    const dueDate = task.sLADueDate || task.oLADueDate || task.dueDate;
                                                    const timeRemaining = getTimeRemaining(dueDate, task.status, task.completedAt);
                                                    
                                                    // Determine badge color based on status and time
                                                    let badgeVariant = 'info'; // Default blue
                                                    if (task.status === 'Completed') {
                                                        badgeVariant = 'success'; // Green for completed
                                                    } else if (dueDate && new Date(dueDate) < new Date()) {
                                                        badgeVariant = 'danger'; // Red for overdue
                                                    } else if (dueDate) {
                                                        const now = new Date();
                                                        const due = new Date(dueDate);
                                                        const hoursRemaining = (due - now) / (1000 * 60 * 60);
                                                        if (hoursRemaining < 24) {
                                                            badgeVariant = 'warning'; // Yellow for less than 1 day
                                                        }
                                                    }
                                                    
                                                    return (
                                                        <Badge bg={badgeVariant} text={badgeVariant === 'warning' ? 'dark' : 'white'}>
                                                            {timeRemaining}
                                                        </Badge>
                                                    );
                                                })()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
                <Card.Footer className="bg-light text-muted small">
                    Showing {filteredTasks.length} of {tasks.length} tasks
                </Card.Footer>
            </Card>
        </Container>
    );
};

export default Tasks;
